import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import Swal from "sweetalert2";
import { adminApi } from "../../repositories/admin";
import { customPackageApi, type Permission } from "../../repositories/customPackage";
import {
  exclusivePermissionApi,
  type ExclusivePermissionGrant,
} from "../../repositories/exclusivePermission";
import {
  buildDefaultValues,
  isFieldInvalid,
  resolveLimit,
  type PermissionValues,
  type PermissionFieldValue,
} from "../../utils/permissions";
import ExclusivePermissionsForm from "../../components/exclusive-permissions/ExclusivePermissionsForm";
import PageHeader from "../../components/ui/PageHeader";
import { HOME_CRUMB } from "../../components/ui/Breadcrumbs";
import Button from "../../components/ui/Button";
import {
  ChevronDownIcon,
  MagnifyingGlassIcon,
  XMarkIcon,
} from "@heroicons/react/24/outline";
import {
  formSectionClass,
  formSectionHeadingClass,
} from "../../components/ui/formStyles";

interface SubscribedUser {
  id: number;
  email: string;
  fullName?: string;
  planName?: string;
}

type UserSearchType = "name" | "email";

// Defensively read the user list from the get-all-users response.
const extractUsers = (data: any): SubscribedUser[] => {
  const raw =
    data?.data?.users ||
    data?.data?.subscribedUsers ||
    data?.data ||
    data?.users ||
    (Array.isArray(data) ? data : []);
  if (!Array.isArray(raw)) return [];
  return raw.map((u: any) => ({
    id: u.id ?? u.userId,
    email: u.email,
    fullName: u.fullName ?? u.name ?? u.full_name,
    planName: u.planName ?? u.plan_name,
  }));
};

const AssignExclusivePermissions = () => {
  const navigate = useNavigate();

  // Search-to-select user picker
  const [searchTerm, setSearchTerm] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [searchType, setSearchType] = useState<UserSearchType>("name");
  const [results, setResults] = useState<SubscribedUser[]>([]);
  const [isSearching, setIsSearching] = useState(false);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState<SubscribedUser | null>(null);
  const pickerRef = useRef<HTMLDivElement>(null);

  // Permissions + form state
  const [permissions, setPermissions] = useState<Permission[]>([]);
  const [values, setValues] = useState<PermissionValues>({});
  const [errors, setErrors] = useState<Record<number, boolean>>({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Debounce the search box.
  useEffect(() => {
    const handler = setTimeout(() => setDebouncedSearch(searchTerm.trim()), 400);
    return () => clearTimeout(handler);
  }, [searchTerm]);

  // Fetch matching subscribed users — only once the admin has typed something.
  const searchUsers = useCallback(async (term: string, type: UserSearchType) => {
    if (!term) {
      setResults([]);
      return;
    }
    try {
      setIsSearching(true);
      const response = await adminApi.getUsers({
        page: 1,
        limit: 10,
        search: term,
        searchType: type,
        type: "subscription",
      });
      setResults(extractUsers(response.data));
    } catch (error: any) {
      console.error("Failed to search subscribed users:", error);
      setResults([]);
    } finally {
      setIsSearching(false);
    }
  }, []);

  useEffect(() => {
    searchUsers(debouncedSearch, searchType);
  }, [debouncedSearch, searchType, searchUsers]);

  // Close the dropdown when clicking outside the picker.
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (pickerRef.current && !pickerRef.current.contains(event.target as Node)) {
        setIsDropdownOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Fetch the backend-driven permission list once.
  useEffect(() => {
    const fetchPermissions = async () => {
      try {
        const response = await customPackageApi.getAllPermissions();
        let permissionsData = [];
        if (response.data?.success && response.data?.data) {
          permissionsData = response.data.data;
        } else if (Array.isArray(response.data)) {
          permissionsData = response.data;
        }
        setPermissions(permissionsData);
        setValues(buildDefaultValues(permissionsData));
      } catch (error) {
        console.error("Failed to fetch permissions:", error);
      }
    };
    fetchPermissions();
  }, []);

  const handleSelectUser = (user: SubscribedUser) => {
    setSelectedUser(user);
    setSearchTerm("");
    setDebouncedSearch("");
    setResults([]);
    setIsDropdownOpen(false);
  };

  const handleFieldChange = (permissionId: number, value: PermissionFieldValue) => {
    setValues((prev) => ({ ...prev, [permissionId]: value }));
    if (errors[permissionId]) {
      setErrors((prev) => ({ ...prev, [permissionId]: false }));
    }
  };

  const handleSubmit = async () => {
    if (!selectedUser) {
      Swal.fire({
        icon: "error",
        title: "Error",
        text: "Please select a user first",
      });
      return;
    }

    // Validate numeric inputs — flag invalid ones with a red border.
    const nextErrors: Record<number, boolean> = {};
    let hasError = false;
    for (const permission of permissions) {
      const value = values[permission.id];
      if (value && isFieldInvalid(permission, value)) {
        nextErrors[permission.id] = true;
        hasError = true;
      }
    }
    if (hasError) {
      setErrors(nextErrors);
      return;
    }
    setErrors({});

    // Build the grant payload.
    const exclusivePermissions: ExclusivePermissionGrant[] = [];
    for (const permission of permissions) {
      const value = values[permission.id];
      if (!value) continue;
      const resolved = resolveLimit(permission, value);
      if (resolved === undefined) continue; // guarded above
      exclusivePermissions.push({ permissionId: permission.id, limit: resolved });
    }

    try {
      setIsSubmitting(true);
      await exclusivePermissionApi.grantPermissions(selectedUser.id, exclusivePermissions);
      Swal.fire({
        icon: "success",
        title: "Success!",
        text: "Exclusive permissions granted successfully",
      });
      navigate("/exclusive-permissions");
    } catch (error: any) {
      Swal.fire({
        icon: "error",
        title: "Error",
        text: error?.response?.data?.message || "Failed to grant permissions",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="py-2 sm:p-6">
      <PageHeader
        breadcrumbs={[HOME_CRUMB, { label: "Exclusive Permissions", to: "/exclusive-permissions" }, { label: "Assign Permissions" }]}
        className="mb-6"
        title="Assign Exclusive Permissions"
        description="Search for a subscribed user, then set their exclusive permissions."
      />

      <div className="space-y-6">
        {/* User picker */}
        <div className={formSectionClass}>
          <h2 className={`mb-4 ${formSectionHeadingClass}`}>Select Subscribed User</h2>

          {selectedUser ? (
            <div className="flex items-center justify-between gap-3 p-4 border rounded-lg border-primary-200 bg-primary-50">
              <div className="min-w-0">
                <p className="text-sm font-medium text-black truncate">
                  {selectedUser.fullName || selectedUser.email}
                </p>
                {selectedUser.fullName && (
                  <p className="text-xs truncate text-neutral-500">{selectedUser.email}</p>
                )}
                {selectedUser.planName && (
                  <p className="text-xs truncate text-neutral-500">{selectedUser.planName}</p>
                )}
              </div>
              <Button
                variant="link"
                size="inline"
                onClick={() => setSelectedUser(null)}
                className="shrink-0"
              >
                Change
              </Button>
            </div>
          ) : (
            <div className="relative" ref={pickerRef}>
              {/*
                Search type + term as one control: a single border, radius and
                focus-within treatment wrap both, with an inset divider between
                them. Same bar as Past Submissions / Master Roster / Contacts.
                The ref wraps the whole control so the results dropdown spans
                its full width rather than just the input half.
              */}
              <div className="flex items-stretch w-full bg-white border rounded-lg border-neutral-300 focus-within:border-primary-500">
                {/* Search Type Selector */}
                <div className="relative shrink-0">
                  <select
                    value={searchType}
                    onChange={(e) => {
                      setSearchType(e.target.value as UserSearchType);
                      setResults([]);
                    }}
                    aria-label="Search subscribed users by field"
                    className="w-[104px] sm:w-auto h-full py-2 pl-3 text-sm truncate bg-transparent border-0 appearance-none cursor-pointer pr-7 text-neutral-600 focus:outline-none focus:ring-0"
                  >
                    <option value="name">Name</option>
                    <option value="email">Email</option>
                  </select>
                  <ChevronDownIcon className="absolute w-4 h-4 -translate-y-1/2 pointer-events-none right-2 top-1/2 text-neutral-400" />
                </div>

                <span aria-hidden="true" className="self-stretch w-px my-1.5 bg-neutral-200" />

                {/* Search Input */}
                <div className="relative flex-1 min-w-0">
                  <MagnifyingGlassIcon className="absolute w-4 h-4 -translate-y-1/2 pointer-events-none left-3 top-1/2 text-neutral-400" />
                  <input
                    type="text"
                    value={searchTerm}
                    onChange={(e) => {
                      setSearchTerm(e.target.value);
                      setIsDropdownOpen(true);
                    }}
                    onFocus={() => setIsDropdownOpen(true)}
                    placeholder={searchType === "name" ? "Search by name..." : "Search by email..."}
                    aria-label="Search subscribed users"
                    className="w-full py-2 text-sm text-black bg-transparent border-0 rounded-r-lg pl-9 pr-9 placeholder-neutral-400 focus:outline-none focus:ring-0"
                  />
                  {searchTerm && (
                    <button
                      onClick={() => setSearchTerm("")}
                      aria-label="Clear search"
                      className="border border-transparent absolute p-0.5 -translate-y-1/2 rounded right-2 top-1/2 text-neutral-400 hover:text-primary-700 hover:bg-primary-50 focus:outline-none focus-visible:border-primary-700"
                    >
                      <XMarkIcon className="w-4 h-4" />
                    </button>
                  )}
                </div>
              </div>

                {isDropdownOpen && searchTerm.trim() && (
                  <div className="absolute z-50 w-full mt-1 overflow-y-auto bg-white border rounded-md shadow-lg border-neutral-200 max-h-64 scrollbar-thin">
                    {isSearching ? (
                      <div className="flex items-center justify-center py-6">
                        <div className="w-6 h-6 border-2 rounded-full border-neutral-200 border-t-primary-700 animate-spin" />
                      </div>
                    ) : results.length === 0 ? (
                      <p className="px-4 py-6 text-sm text-center text-neutral-500">
                        No subscribed users found
                      </p>
                    ) : (
                      <div className="divide-y divide-neutral-100">
                        {results.map((user) => (
                          <button
                            key={user.id}
                            onClick={() => handleSelectUser(user)}
                            className="flex items-center justify-between w-full gap-3 px-4 py-3 text-left transition-colors hover:bg-primary-50 focus:outline-none focus-visible:bg-primary-50"
                          >
                            <span className="flex flex-col min-w-0">
                              <span className="text-sm font-medium text-black truncate">
                                {user.fullName || user.email}
                              </span>
                              {user.fullName && (
                                <span className="text-xs truncate text-neutral-500">{user.email}</span>
                              )}
                            </span>
                            {user.planName && (
                              <span className="text-xs shrink-0 text-neutral-500">{user.planName}</span>
                            )}
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                )}
            </div>
          )}
        </div>

        {/* Permissions */}
        <div className={formSectionClass}>
          <h2 className={`mb-4 ${formSectionHeadingClass}`}>Configure Permissions</h2>
          {!selectedUser ? (
            <p className="py-8 text-sm text-center text-neutral-500">
              Select a user to configure permissions
            </p>
          ) : (
            <ExclusivePermissionsForm
              permissions={permissions}
              values={values}
              errors={errors}
              onChange={handleFieldChange}
            />
          )}
        </div>

        {/* Submit */}
        <div className="flex justify-end">
          <Button onClick={handleSubmit} loading={isSubmitting} disabled={!selectedUser}>
            {isSubmitting ? "Saving..." : "Save"}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default AssignExclusivePermissions;
